// WGSL for the physical simulation step (WebGPU compute). Generated from the
// CPU reference modules' constants so the two backends share one set of
// physical parameters. Each entry point mirrors a CPU function; comments name
// it. Semantics are kept order-independent so results match the CPU reference
// within float tolerance (see gpu/validation.js).
//
// Buffers (all f32 unless noted, N = W*H):
//   S    scalar fields, field-major: S[f*N + i]            (SCALAR_FIELDS order)
//   P    pigment amounts: P[(layer*8 + k)*N + i]; layer 0 wet, 1 deposited, 2 body
//   R    dried spectral stack, cell-major: R[i*BANDS + b]
//   surf surface: height, absorbency, capillary, pinning (field-major)
//   X    scratch: fx, fy, scale, exposure, tmp, ratio[8], old[8]
//   T    u32 atomics: active[nt], region[nt], dirty[nt], newActive[nt], globals[16], regionList[nt]
//   ind  indirect dispatch arguments for region kernels (written on the GPU)

import { BAND_COUNT } from '../pigments/ColourConversion.js';
import { PIGMENT_SLOTS, TILE_SIZE, scalarFieldNames } from '../core/PaintState.js';
import { DEFAULT_WATER_PARAMS, WATER_WET_DEPTH, PAPER_WET_SATURATION } from '../physics/WaterTransport.js';
import { DEFAULT_PAPER_PARAMS } from '../physics/PaperAbsorption.js';
import { DEFAULT_PIGMENT_PARAMS } from '../physics/PigmentExchange.js';
import { DEFAULT_BODY_PARAMS, BODY_PRESENT, MAX_BODY_SUBSTEPS } from '../physics/ViscousPaint.js';

export const FIELD = Object.freeze(Object.fromEntries(scalarFieldNames().map((name, index) => [name, index])));
export const SCRATCH = Object.freeze({ fx: 0, fy: 1, scale: 2, exposure: 3, tmp: 4, ratio: 5, old: 5 + PIGMENT_SLOTS, count: 5 + 2 * PIGMENT_SLOTS });
export const GLOBAL = Object.freeze({ hMaxBits: 0, rateBits: 1, nWater: 2, nBody: 3, hWaterBits: 4, hBodyBits: 5, regionCount: 6, count: 16 });
// Region kernels run as indirect dispatches: TILE_WORKGROUPS 8x8 workgroups per region tile.
export const TILE_WORKGROUPS = (TILE_SIZE / 8) * (TILE_SIZE / 8);
export const PIGMENT_RECORD = 28; // K[12], S[12], granulation, staining, density, present

// Upper bound on water substeps: the depth used for stability is capped.
export function maxWaterSubsteps(dt) {
  const p = DEFAULT_WATER_PARAMS;
  const stable = Math.min(p.maxSubstep, 0.15 / (p.mobility * p.mobilityDepthCap ** 3 + 1e-9));
  return Math.max(1, Math.min(64, Math.ceil(dt / stable)));
}

const f = (v) => {
  const s = Number(v).toString();
  return /[.eE]/.test(s) ? s : `${s}.0`;
};

export function buildSimShader() {
  const w = DEFAULT_WATER_PARAMS;
  const p = DEFAULT_PAPER_PARAMS;
  const g = DEFAULT_PIGMENT_PARAMS;
  const b = DEFAULT_BODY_PARAMS;
  const F = FIELD;
  return /* wgsl */ `
struct Params {
  W: u32, H: u32, tilesX: u32, tilesY: u32,
  dt: f32, tiltX: f32, tiltY: f32, evapScale: f32,
  dryingScale: f32, slotsMask: u32, surfaceSheen: f32, granulationScale: f32,
};
struct Sub { s: u32, mode: u32, pad0: u32, pad1: u32 };

@group(0) @binding(0) var<uniform> prm: Params;
@group(0) @binding(1) var<storage, read_write> S: array<f32>;
@group(0) @binding(2) var<storage, read_write> P: array<f32>;
@group(0) @binding(3) var<storage, read_write> R: array<f32>;
@group(0) @binding(4) var<storage, read> surf: array<f32>;
@group(0) @binding(5) var<storage, read_write> X: array<f32>;
@group(0) @binding(6) var<storage, read_write> T: array<atomic<u32>>;
@group(0) @binding(7) var<uniform> pig: array<vec4<f32>, ${(PIGMENT_SLOTS * PIGMENT_RECORD) / 4}>;
// Own bind group: the buffer is consumed as indirect arguments by later
// dispatches and must not be bound as writable storage in those.
@group(2) @binding(0) var<storage, read_write> ind: array<u32, 4>;
@group(1) @binding(0) var<uniform> sub: Sub;

const BANDS: u32 = ${BAND_COUNT}u;
const SLOTS: u32 = ${PIGMENT_SLOTS}u;
const TILE: u32 = ${TILE_SIZE}u;
const WET_DEPTH: f32 = ${f(WATER_WET_DEPTH)};
const PAPER_WET: f32 = ${f(PAPER_WET_SATURATION)};
const BODY_PRESENT: f32 = ${f(BODY_PRESENT)};

fn N() -> u32 { return prm.W * prm.H; }
fn nt() -> u32 { return prm.tilesX * prm.tilesY; }
fn sf(field: u32, i: u32) -> f32 { return S[field * N() + i]; }
fn sset(field: u32, i: u32, v: f32) { S[field * N() + i] = v; }
fn pi(layer: u32, k: u32, i: u32) -> u32 { return (layer * SLOTS + k) * N() + i; }
fn xi(slot: u32, i: u32) -> u32 { return slot * N() + i; }
fn surfH(i: u32) -> f32 { return surf[i]; }
fn pigK(k: u32, band: u32) -> f32 { let o = k * ${PIGMENT_RECORD}u + band; return pig[o / 4u][o % 4u]; }
fn pigS(k: u32, band: u32) -> f32 { let o = k * ${PIGMENT_RECORD}u + 12u + band; return pig[o / 4u][o % 4u]; }
fn pigProp(k: u32, which: u32) -> f32 { let o = k * ${PIGMENT_RECORD}u + 24u + which; return pig[o / 4u][o % 4u]; }
fn slotOn(k: u32) -> bool { return (prm.slotsMask & (1u << k)) != 0u; }
fn tileOf(i: u32) -> u32 { let x = i % prm.W; let y = i / prm.W; return (y / TILE) * prm.tilesX + x / TILE; }
fn inRegion(i: u32) -> bool { return atomicLoad(&T[nt() + tileOf(i)]) == 1u; }
fn gload(k: u32) -> u32 { return atomicLoad(&T[4u * nt() + k]); }

// Cell of a thread in an indirect region dispatch, or N() when out of bounds.
// Workgroup (x, y) covers 8x8 block x of region tile regionList[y]. Two
// dimensions keep every count under the 65535-per-dimension dispatch limit
// (an 8K canvas has ~32k tiles).
fn regionCell(wg: vec3<u32>, lid: vec3<u32>) -> u32 {
  let tile = atomicLoad(&T[4u * nt() + ${GLOBAL.count}u + wg.y]);
  let sub = wg.x;
  let x = (tile % prm.tilesX) * TILE + (sub % ${TILE_SIZE / 8}u) * 8u + lid.x;
  let y = (tile / prm.tilesX) * TILE + (sub / ${TILE_SIZE / 8}u) * 8u + lid.y;
  if (x >= prm.W || y >= prm.H) { return N(); }
  return y * prm.W + x;
}

// ---------------------------------------------------------------- planning
// processingTiles(): active tiles dilated by one tile.
@compute @workgroup_size(64)
fn buildRegion(@builtin(global_invocation_id) gid: vec3<u32>) {
  let t = gid.x;
  if (t >= nt()) { return; }
  let tx = i32(t % prm.tilesX);
  let ty = i32(t / prm.tilesX);
  var on = 0u;
  for (var dy = -1; dy <= 1; dy++) {
    for (var dx = -1; dx <= 1; dx++) {
      let x = tx + dx;
      let y = ty + dy;
      if (x < 0 || y < 0 || x >= i32(prm.tilesX) || y >= i32(prm.tilesY)) { continue; }
      if (atomicLoad(&T[u32(y) * prm.tilesX + u32(x)]) == 1u) { on = 1u; }
    }
  }
  atomicStore(&T[nt() + t], on);
  if (on == 1u) {
    let slot = atomicAdd(&T[4u * nt() + ${GLOBAL.regionCount}u], 1u);
    atomicStore(&T[4u * nt() + ${GLOBAL.count}u + slot], t);
  }
}

@compute @workgroup_size(1)
fn resetGlobals() {
  for (var k = 0u; k < ${GLOBAL.count}u; k++) { atomicStore(&T[4u * nt() + k], 0u); }
}

// Indirect arguments: one 8x8 workgroup per 8x8 block of every region tile.
@compute @workgroup_size(1)
fn writeIndirect() {
  ind[0] = ${TILE_WORKGROUPS}u;
  ind[1] = gload(${GLOBAL.regionCount}u);
  ind[2] = 1u;
}

// Maxima for the stability limits (positive floats order like their bits).
@compute @workgroup_size(8, 8)
fn reduceMaxima(@builtin(workgroup_id) wg: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
  let i = regionCell(wg, lid);
  if (i >= N()) { return; }
  let h = min(${f(w.mobilityDepthCap)}, sf(${F.water}u, i));
  if (h > 0.0) { atomicMax(&T[4u * nt() + ${GLOBAL.hMaxBits}u], bitcast<u32>(h)); }
  let v = sf(${F.bodyVol}u, i);
  if (v > BODY_PRESENT) {
    let o = sf(${F.bodyOpen}u, i);
    let rate = sf(${F.bodyMob}u, i) * o * o * v * v * v;
    if (rate > 0.0) { atomicMax(&T[4u * nt() + ${GLOBAL.rateBits}u], bitcast<u32>(rate)); }
  }
}

// stepWater()/stepBodyFlow() substep counts, computed on the GPU (no readback).
@compute @workgroup_size(1)
fn planSubsteps() {
  let hMax = bitcast<f32>(gload(${GLOBAL.hMaxBits}u));
  var nW = 0u;
  if (hMax >= 1e-5) {
    let stable = min(${f(w.maxSubstep)}, 0.15 / (${f(w.mobility)} * hMax * hMax * hMax + 1e-9));
    nW = u32(max(1.0, min(64.0, ceil(prm.dt / stable))));
  }
  let rate = bitcast<f32>(gload(${GLOBAL.rateBits}u));
  var nB = 0u;
  if (rate > 1e-7) {
    let stable = min(${f(b.maxSubstep)}, 0.2 / rate);
    nB = u32(max(1.0, min(${f(MAX_BODY_SUBSTEPS)}, ceil(prm.dt / stable))));
  }
  atomicStore(&T[4u * nt() + ${GLOBAL.nWater}u], nW);
  atomicStore(&T[4u * nt() + ${GLOBAL.nBody}u], nB);
  atomicStore(&T[4u * nt() + ${GLOBAL.hWaterBits}u], bitcast<u32>(select(0.0, prm.dt / f32(max(nW, 1u)), nW > 0u)));
  atomicStore(&T[4u * nt() + ${GLOBAL.hBodyBits}u], bitcast<u32>(select(0.0, prm.dt / f32(max(nB, 1u)), nB > 0u)));
}

// sub.mode: 0 water substep, 1 body substep, 2 rim replenishment (always runs once).
fn transportActive() -> bool {
  if (sub.mode == 0u) { return sub.s < gload(${GLOBAL.nWater}u); }
  if (sub.mode == 1u) { return sub.s < gload(${GLOBAL.nBody}u); }
  return true;
}
fn primaryField() -> u32 { return select(${F.water}u, ${F.bodyVol}u, sub.mode == 1u); }
fn carriedLayer() -> u32 { return select(0u, 2u, sub.mode == 1u); }

// ---------------------------------------------------------------- water faces (WaterTransport.faceFlux)
fn isWet(i: u32) -> bool { return sf(${F.water}u, i) > WET_DEPTH || sf(${F.paperSat}u, i) > PAPER_WET; }
fn etaWater(i: u32) -> f32 {
  return sf(${F.water}u, i) + ${f(w.reliefScale)} * surfH(i) + sf(${F.dryHeight}u, i) * 0.02 + sf(${F.bodyVol}u, i) * 0.02;
}
fn waterFace(i: u32, j: u32, tilt: f32, hs: f32) -> f32 {
  let drive = etaWater(i) - etaWater(j) + tilt;
  if (drive == 0.0) { return 0.0; }
  let src = select(j, i, drive > 0.0);
  let dst = select(i, j, drive > 0.0);
  let h = sf(${F.water}u, src);
  if (h < 1e-6) { return 0.0; }
  if (!isWet(dst)) {
    if (h < ${f(w.pinDepth)} * surf[3u * N() + dst]) { return 0.0; }
  }
  let hc = min(h, ${f(w.mobilityDepthCap)});
  let amount = ${f(w.mobility)} * hc * hc * hc * abs(drive) * hs;
  return select(-amount, amount, drive > 0.0);
}

// ---------------------------------------------------------------- body faces (ViscousPaint.stepBodyFlow)
fn etaBody(i: u32) -> f32 { return sf(${F.bodyVol}u, i) + sf(${F.dryHeight}u, i) + ${f(b.reliefScale)} * surfH(i); }
fn bodyFace(i: u32, j: u32, tilt: f32, hs: f32) -> f32 {
  let drive = etaBody(i) - etaBody(j) + tilt;
  if (drive == 0.0) { return 0.0; }
  let src = select(j, i, drive > 0.0);
  let v = sf(${F.bodyVol}u, src);
  if (v < BODY_PRESENT) { return 0.0; }
  let o = sf(${F.bodyOpen}u, src);
  let excess = abs(drive) - sf(${F.bodyYield}u, src) / max(v, 0.02);
  if (excess <= 0.0) { return 0.0; }
  let amount = sf(${F.bodyMob}u, src) * o * o * v * v * v * excess * hs;
  return select(-amount, amount, drive > 0.0);
}

@compute @workgroup_size(8, 8)
fn transportFaces(@builtin(workgroup_id) wg: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
  let i = regionCell(wg, lid);
  if (i >= N() || !transportActive()) { return; }
  let x = i % prm.W;
  let y = i / prm.W;
  var fxv = 0.0;
  var fyv = 0.0;
  if (sub.mode == 0u) {
    let hs = bitcast<f32>(gload(${GLOBAL.hWaterBits}u));
    if (x + 1u < prm.W) { fxv = waterFace(i, i + 1u, prm.tiltX, hs); }
    if (y + 1u < prm.H) { fyv = waterFace(i, i + prm.W, prm.tiltY, hs); }
  } else if (sub.mode == 1u) {
    let hs = bitcast<f32>(gload(${GLOBAL.hBodyBits}u));
    if (x + 1u < prm.W) { fxv = bodyFace(i, i + 1u, prm.tiltX * ${f(b.tiltCoupling)}, hs); }
    if (y + 1u < prm.H) { fyv = bodyFace(i, i + prm.W, prm.tiltY * ${f(b.tiltCoupling)}, hs); }
  } else {
    if (x + 1u < prm.W) { fxv = rimFace(i, i + 1u); }
    if (y + 1u < prm.H) { fyv = rimFace(i, i + prm.W); }
  }
  X[xi(${SCRATCH.fx}u, i)] = fxv;
  X[xi(${SCRATCH.fy}u, i)] = fyv;
}

// FluxTransport.limitOutflow: faces of out-of-region cells count as zero.
@compute @workgroup_size(8, 8)
fn transportScale(@builtin(workgroup_id) wg: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
  let i = regionCell(wg, lid);
  if (i >= N() || !transportActive()) { return; }
  let x = i % prm.W;
  let y = i / prm.W;
  var out = 0.0;
  let fr = X[xi(${SCRATCH.fx}u, i)];
  let fb = X[xi(${SCRATCH.fy}u, i)];
  if (fr > 0.0) { out += fr; }
  if (fb > 0.0) { out += fb; }
  if (x > 0u && inRegion(i - 1u)) { let fl = X[xi(${SCRATCH.fx}u, i - 1u)]; if (fl < 0.0) { out -= fl; } }
  if (y > 0u && inRegion(i - prm.W)) { let ft = X[xi(${SCRATCH.fy}u, i - prm.W)]; if (ft < 0.0) { out -= ft; } }
  let have = sf(primaryField(), i);
  var scale = 1.0;
  if (out > have) { scale = select(0.0, have / out, have > 0.0); }
  X[xi(${SCRATCH.scale}u, i)] = scale;
}

// Scales own faces by the source cell's limiter and records carried ratios.
@compute @workgroup_size(8, 8)
fn transportRatios(@builtin(workgroup_id) wg: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
  let i = regionCell(wg, lid);
  if (i >= N() || !transportActive()) { return; }
  let x = i % prm.W;
  let y = i / prm.W;
  if (x + 1u < prm.W) {
    let f = X[xi(${SCRATCH.fx}u, i)];
    if (f > 0.0) { X[xi(${SCRATCH.fx}u, i)] = f * X[xi(${SCRATCH.scale}u, i)]; }
    else if (f < 0.0) { X[xi(${SCRATCH.fx}u, i)] = f * X[xi(${SCRATCH.scale}u, i + 1u)]; }
  }
  if (y + 1u < prm.H) {
    let f = X[xi(${SCRATCH.fy}u, i)];
    if (f > 0.0) { X[xi(${SCRATCH.fy}u, i)] = f * X[xi(${SCRATCH.scale}u, i)]; }
    else if (f < 0.0) { X[xi(${SCRATCH.fy}u, i)] = f * X[xi(${SCRATCH.scale}u, i + prm.W)]; }
  }
  let primary = sf(primaryField(), i);
  for (var k = 0u; k < SLOTS; k++) {
    var r = 0.0;
    if (primary > 1e-9) { r = P[pi(carriedLayer(), k, i)] / primary; }
    X[xi(${SCRATCH.ratio}u + k, i)] = r;
  }
}

// FluxTransport.carryAlongFaces + applyFaces, in gather form.
@compute @workgroup_size(8, 8)
fn transportApply(@builtin(workgroup_id) wg: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
  let i = regionCell(wg, lid);
  if (i >= N() || !transportActive()) { return; }
  let x = i % prm.W;
  let y = i / prm.W;
  // Net outflow through each face (positive = leaving i), and the source cell of each.
  var faces = array<f32, 4>(0.0, 0.0, 0.0, 0.0);
  var others = array<u32, 4>(i, i, i, i);
  if (x + 1u < prm.W) { faces[0] = X[xi(${SCRATCH.fx}u, i)]; others[0] = i + 1u; }
  if (y + 1u < prm.H) { faces[1] = X[xi(${SCRATCH.fy}u, i)]; others[1] = i + prm.W; }
  if (x > 0u && inRegion(i - 1u)) { faces[2] = -X[xi(${SCRATCH.fx}u, i - 1u)]; others[2] = i - 1u; }
  if (y > 0u && inRegion(i - prm.W)) { faces[3] = -X[xi(${SCRATCH.fy}u, i - prm.W)]; others[3] = i - prm.W; }
  let field = primaryField();
  var net = 0.0;
  for (var d = 0u; d < 4u; d++) { net += faces[d]; }
  sset(field, i, max(0.0, sf(field, i) - net));
  for (var k = 0u; k < SLOTS; k++) {
    if (!slotOn(k)) { continue; }
    var v = P[pi(carriedLayer(), k, i)];
    for (var d = 0u; d < 4u; d++) {
      let fl = faces[d];
      if (fl > 0.0) { v -= fl * X[xi(${SCRATCH.ratio}u + k, i)]; }
      else if (fl < 0.0) { v -= fl * X[xi(${SCRATCH.ratio}u + k, others[d])]; }
    }
    P[pi(carriedLayer(), k, i)] = max(0.0, v);
  }
}

// WaterTransport.diffusePigment: snapshot, then symmetric face exchange.
@compute @workgroup_size(8, 8)
fn diffuseSnapshot(@builtin(workgroup_id) wg: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
  let i = regionCell(wg, lid);
  if (i >= N() || sub.s >= gload(${GLOBAL.nWater}u)) { return; }
  for (var k = 0u; k < SLOTS; k++) { X[xi(${SCRATCH.old}u + k, i)] = P[pi(0u, k, i)]; }
}
fn diffusionFlux(k: u32, a: u32, b: u32, rate: f32) -> f32 {
  let ha = sf(${F.water}u, a);
  let hb = sf(${F.water}u, b);
  if (ha < WET_DEPTH || hb < WET_DEPTH) { return 0.0; }
  let ga = X[xi(${SCRATCH.old}u + k, a)];
  let gb = X[xi(${SCRATCH.old}u + k, b)];
  let moved = rate * min(ha, hb) * (ga / ha - gb / hb);
  return select(max(moved, -0.125 * gb), min(moved, 0.125 * ga), moved > 0.0);
}
@compute @workgroup_size(8, 8)
fn diffuseApply(@builtin(workgroup_id) wg: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
  let i = regionCell(wg, lid);
  if (i >= N() || sub.s >= gload(${GLOBAL.nWater}u)) { return; }
  if (sf(${F.water}u, i) < WET_DEPTH) { return; }
  let rate = ${f(w.pigmentDiffusion)} * bitcast<f32>(gload(${GLOBAL.hWaterBits}u));
  let x = i % prm.W;
  let y = i / prm.W;
  for (var k = 0u; k < SLOTS; k++) {
    if (!slotOn(k)) { continue; }
    var delta = 0.0;
    // Neighbours outside the region hold no water, so their faces are zero.
    if (x > 0u) { delta += diffusionFlux(k, i - 1u, i, rate); }
    if (x + 1u < prm.W) { delta -= diffusionFlux(k, i, i + 1u, rate); }
    if (y > 0u) { delta += diffusionFlux(k, i - prm.W, i, rate); }
    if (y + 1u < prm.H) { delta -= diffusionFlux(k, i, i + prm.W, rate); }
    P[pi(0u, k, i)] = max(0.0, X[xi(${SCRATCH.old}u + k, i)] + delta);
  }
}

// ---------------------------------------------------------------- paper (PaperAbsorption.stepPaper)
@compute @workgroup_size(8, 8)
fn paperExposure(@builtin(workgroup_id) wg: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
  let i = regionCell(wg, lid);
  if (i >= N()) { return; }
  let r = ${p.edgeRadius}i;
  let x = i32(i % prm.W);
  let y = i32(i / prm.W);
  var dry = 0.0;
  for (var dy = -r; dy <= r; dy++) {
    let yy = y + dy;
    if (yy < 0 || yy >= i32(prm.H) || !inRegion(u32(yy) * prm.W + u32(x))) { dry += f32(2 * r + 1); continue; }
    for (var dx = -r; dx <= r; dx++) {
      let xx = x + dx;
      if (xx < 0 || xx >= i32(prm.W)) { dry += 1.0; continue; }
      if (sf(${F.water}u, u32(yy) * prm.W + u32(xx)) < WET_DEPTH) { dry += 1.0; }
    }
  }
  X[xi(${SCRATCH.exposure}u, i)] = dry / f32((2 * r + 1) * (2 * r + 1));
}

// replenishEdges(): a rim cell's demand coefficient on its deeper neighbours.
fn rimCoefficient(c: u32) -> f32 {
  if (!inRegion(c)) { return 0.0; }
  let e = X[xi(${SCRATCH.exposure}u, c)];
  let h = sf(${F.water}u, c);
  if (e <= 0.0 || h < ${f(p.pinRelease)}) { return 0.0; }
  let x = c % prm.W;
  let y = c / prm.W;
  var total = 0.0;
  if (x > 0u) { total += max(0.0, sf(${F.water}u, c - 1u) - h); }
  if (x + 1u < prm.W) { total += max(0.0, sf(${F.water}u, c + 1u) - h); }
  if (y > 0u) { total += max(0.0, sf(${F.water}u, c - prm.W) - h); }
  if (y + 1u < prm.H) { total += max(0.0, sf(${F.water}u, c + prm.W) - h); }
  if (total <= 0.0) { return 0.0; }
  let t = min(1.0, (h - ${f(p.pinRelease)}) / (3.0 * ${f(p.pinRelease)}));
  let pinned = t * t * (3.0 - 2.0 * t);
  let demandScale = prm.dt * ${f(p.evaporation)} * prm.evapScale * ${f(p.edgeEvaporation)} * ${f(p.capillaryFeed)};
  return min(demandScale * sqrt(e) * pinned, 0.1 * total) / total;
}
fn rimFace(a: u32, b: u32) -> f32 {
  let ha = sf(${F.water}u, a);
  let hb = sf(${F.water}u, b);
  // b deeper: a pulls from b (flow b -> a, negative); a deeper: b pulls from a.
  if (hb > ha) { return -rimCoefficient(a) * (hb - ha); }
  if (ha > hb) { return rimCoefficient(b) * (ha - hb); }
  return 0.0;
}

@compute @workgroup_size(8, 8)
fn paperLocal(@builtin(workgroup_id) wg: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
  let i = regionCell(wg, lid);
  if (i >= N()) { return; }
  var h = sf(${F.water}u, i);
  var s = sf(${F.paperSat}u, i);
  if (h > 0.0) {
    let room = max(0.0, 1.0 - s / ${f(p.capacity)});
    let soak = min(h, prm.dt * ${f(p.absorption)} * surf[N() + i] * room * min(1.0, h / 0.05 + 0.2));
    h -= soak;
    s += soak;
    let evap = min(h, prm.dt * ${f(p.evaporation)} * prm.evapScale * (1.0 + ${f(p.edgeEvaporation)} * sqrt(X[xi(${SCRATCH.exposure}u, i)])));
    h -= evap;
    if (h < 1e-6) { h = 0.0; }
  }
  if (s > 0.0) {
    let evap = min(s, prm.dt * ${f(p.paperEvaporation)} * prm.evapScale * select(1.0, 0.2, h > WET_DEPTH));
    s -= evap;
  }
  sset(${F.water}u, i, h);
  sset(${F.paperSat}u, i, s);
  X[xi(${SCRATCH.tmp}u, i)] = s;
}

fn wickMove(a: u32, b: u32, rate: f32) -> f32 {
  let sa = X[xi(${SCRATCH.tmp}u, a)];
  let sb = X[xi(${SCRATCH.tmp}u, b)];
  let diff = sa - sb;
  if (diff == 0.0) { return 0.0; }
  let source = select(sb, sa, diff > 0.0);
  if (source < ${f(p.wickMinimum)}) { return 0.0; }
  let conductance = 0.5 * (surf[2u * N() + a] + surf[2u * N() + b]);
  return rate * conductance * diff * 0.5;
}
@compute @workgroup_size(8, 8)
fn paperWick(@builtin(workgroup_id) wg: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
  let i = regionCell(wg, lid);
  if (i >= N()) { return; }
  let rate = min(0.2, prm.dt * ${f(p.wicking)});
  let x = i % prm.W;
  let y = i / prm.W;
  var delta = 0.0;
  if (x + 1u < prm.W && inRegion(i + 1u)) { delta -= wickMove(i, i + 1u, rate); }
  if (y + 1u < prm.H && inRegion(i + prm.W)) { delta -= wickMove(i, i + prm.W, rate); }
  if (x > 0u && inRegion(i - 1u)) { delta += wickMove(i - 1u, i, rate); }
  if (y > 0u && inRegion(i - prm.W)) { delta += wickMove(i - prm.W, i, rate); }
  sset(${F.paperSat}u, i, max(0.0, X[xi(${SCRATCH.tmp}u, i)] + delta));
}

// ---------------------------------------------------------------- PigmentExchange.stepPigmentExchange
@compute @workgroup_size(8, 8)
fn pigmentExchange(@builtin(workgroup_id) wg: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
  let i = regionCell(wg, lid);
  if (i >= N()) { return; }
  let dt = prm.dt;
  let h = sf(${F.water}u, i);
  let wet = h > WET_DEPTH;
  let damp = wet || sf(${F.paperSat}u, i) > PAPER_WET;
  var bset = sf(${F.binderSet}u, i);
  if (!damp) { bset += (1.0 - bset) * (1.0 - exp(-${f(g.binderSetting)} * dt)); }
  else if (wet) { bset -= bset * sf(${F.depRewet}u, i) * (1.0 - exp(-${f(g.binderRedissolve)} * dt)); }
  sset(${F.binderSet}u, i, bset);
  let valley = 1.0 - surfH(i);
  let t1 = 1.0 / (1.0 + h / 0.08);
  let thin = 0.05 + 0.95 * t1 * t1 * t1;
  for (var k = 0u; k < SLOTS; k++) {
    if (!slotOn(k)) { continue; }
    var gv = P[pi(0u, k, i)];
    var dv = P[pi(1u, k, i)];
    if (gv > 0.0) {
      if (!wet) {
        dv += gv;
        gv = 0.0;
      } else {
        let granular = max(0.0, 1.0 + 2.2 * pigProp(k, 0u) * ${f(g.granulation)} * prm.granulationScale * (valley - 0.5));
        let rate = ${f(g.settling)} * pigProp(k, 2u) * thin * granular;
        let settled = gv * (1.0 - exp(-rate * dt));
        gv -= settled;
        dv += settled;
      }
    }
    if (wet && dv > 0.0) {
      let looseness = (1.0 - pigProp(k, 1u)) * (1.0 - bset);
      let lifted = dv * (1.0 - exp(-${f(g.lifting)} * looseness * (h / (h + 0.05)) * dt));
      dv -= lifted;
      gv += lifted;
    }
    P[pi(0u, k, i)] = gv;
    P[pi(1u, k, i)] = dv;
  }
}

// ---------------------------------------------------------------- KM compositing (SpectralMixing)
fn qCothQ(q: f32) -> f32 {
  if (q < 1e-3) { return 1.0 + q * q / 3.0; }
  if (q > 20.0) { return q; }
  return q / tanh(q);
}
fn kmLayer(KX: f32, SX: f32, rg: f32) -> f32 {
  let k = max(KX, 0.0);
  let s = max(SX, 0.0);
  if (k < 1e-9 && s < 1e-9) { return rg; }
  if (s < 1e-7) { return rg * exp(-2.0 * k); }
  let q = sqrt(k * k + 2.0 * k * s);
  let qc = qCothQ(q);
  let r = (s - rg * (s + k - qc)) / (s + k - rg * s + qc);
  return clamp(r, 0.0, 1.0);
}
fn compositeLayerIntoStack(i: u32, layer: u32) {
  var any = false;
  for (var k = 0u; k < SLOTS; k++) { if (slotOn(k) && P[pi(layer, k, i)] > 1e-7) { any = true; } }
  if (!any) { return; }
  for (var band = 0u; band < BANDS; band++) {
    var KX = 0.0;
    var SX = 0.0;
    for (var k = 0u; k < SLOTS; k++) {
      if (!slotOn(k)) { continue; }
      let a = P[pi(layer, k, i)];
      if (a <= 1e-7) { continue; }
      KX += a * pigK(k, band);
      SX += a * pigS(k, band);
    }
    R[i * BANDS + band] = kmLayer(KX, SX, R[i * BANDS + band]);
  }
}

// ViscousPaint.cureBodyAt
fn cureBody(i: u32) {
  compositeLayerIntoStack(i, 1u);
  for (var k = 0u; k < SLOTS; k++) { P[pi(1u, k, i)] = 0.0; }
  compositeLayerIntoStack(i, 2u);
  sset(${F.dryHeight}u, i, sf(${F.dryHeight}u, i) + sf(${F.bodyVol}u, i) * sf(${F.bodySolids}u, i));
  sset(${F.bodyVol}u, i, 0.0);
  sset(${F.bodyOpen}u, i, 0.0);
  for (var k = 0u; k < SLOTS; k++) { P[pi(2u, k, i)] = 0.0; }
}

// ViscousPaint.stepBodyDrying
@compute @workgroup_size(8, 8)
fn bodyDrying(@builtin(workgroup_id) wg: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
  let i = regionCell(wg, lid);
  if (i >= N()) { return; }
  let v = sf(${F.bodyVol}u, i);
  if (v <= BODY_PRESENT) {
    if (v > 0.0) { cureBody(i); }
    return;
  }
  let thickness = 1.0 / (1.0 + 0.6 * v);
  let open = sf(${F.bodyOpen}u, i) - prm.dt * sf(${F.bodyDry}u, i) * prm.dryingScale * thickness;
  sset(${F.bodyOpen}u, i, open);
  if (open <= 0.0) { cureBody(i); }
}

// ---------------------------------------------------------------- activity (region.refreshActivity)
fn cellActive(i: u32) -> bool {
  if (sf(${F.water}u, i) > 1e-5 || sf(${F.paperSat}u, i) > 1e-4) { return true; }
  if (sf(${F.bodyVol}u, i) > 0.0) { return true; }
  return sf(${F.depRewet}u, i) > 0.0 && sf(${F.binderSet}u, i) < 0.95;
}
@compute @workgroup_size(8, 8)
fn activityCells(@builtin(workgroup_id) wg: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
  let i = regionCell(wg, lid);
  if (i >= N()) { return; }
  if (cellActive(i)) { atomicStore(&T[3u * nt() + tileOf(i)], 1u); }
}
@compute @workgroup_size(64)
fn activityTiles(@builtin(global_invocation_id) gid: vec3<u32>) {
  let t = gid.x;
  if (t >= nt()) { return; }
  if (atomicLoad(&T[nt() + t]) != 1u) { return; }
  let was = atomicLoad(&T[t]);
  let now = atomicLoad(&T[3u * nt() + t]);
  if (was == 1u || now == 1u) { atomicStore(&T[2u * nt() + t], 1u); }
  atomicStore(&T[t], now);
  atomicStore(&T[3u * nt() + t], 0u);
}

// PaintEngine.dryAll
@compute @workgroup_size(8, 8)
fn dryAll(@builtin(global_invocation_id) gid: vec3<u32>) {
  if (gid.x >= prm.W || gid.y >= prm.H) { return; }
  let i = gid.y * prm.W + gid.x;
  sset(${F.water}u, i, 0.0);
  sset(${F.paperSat}u, i, 0.0);
  for (var k = 0u; k < SLOTS; k++) {
    let gv = P[pi(0u, k, i)];
    if (gv > 0.0) { P[pi(1u, k, i)] += gv; P[pi(0u, k, i)] = 0.0; }
  }
  sset(${F.binderSet}u, i, 1.0);
  if (sf(${F.bodyVol}u, i) > 0.0) { cureBody(i); }
  if (gid.x % TILE == 0u && gid.y % TILE == 0u) {
    let t = tileOf(i);
    atomicStore(&T[t], 0u);
    atomicStore(&T[2u * nt() + t], 1u);
  }
}
`;
}
